import React, { useEffect, useState, useCallback } from 'react'
import { ToastMessage } from '../../types'

interface ToastContainerProps {
  toasts: ToastMessage[]
  onRemoveToast: (id: string) => void
}

export default function ToastContainer({ toasts, onRemoveToast }: ToastContainerProps) {
  const [isExiting, setIsExiting] = useState<Set<string>>(new Set())

  const handleRemove = useCallback((id: string) => {
    setIsExiting(prev => new Set(prev).add(id))
    setTimeout(() => {
      onRemoveToast(id)
      setIsExiting(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }, 300)
  }, [onRemoveToast])

  useEffect(() => {
    toasts.forEach(toast => {
      const timer = setTimeout(() => {
        handleRemove(toast.id)
      }, 5000)
      return () => clearTimeout(timer)
    })
  }, [toasts, handleRemove])

  const getToastStyles = (type: ToastMessage['type']) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20',
          border: 'border-emerald-200 dark:border-emerald-800',
          icon: 'text-emerald-600 dark:text-emerald-400',
          iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
          text: 'text-gray-900 dark:text-gray-100'
        }
      case 'error':
        return {
          bg: 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20',
          border: 'border-red-200 dark:border-red-800',
          icon: 'text-red-600 dark:text-red-400',
          iconBg: 'bg-red-100 dark:bg-red-900/50',
          text: 'text-gray-900 dark:text-gray-100'
        }
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20',
          border: 'border-amber-200 dark:border-amber-800',
          icon: 'text-amber-600 dark:text-amber-400',
          iconBg: 'bg-amber-100 dark:bg-amber-900/50',
          text: 'text-gray-900 dark:text-gray-100'
        }
      case 'info':
      default:
        return {
          bg: 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          icon: 'text-blue-600 dark:text-blue-400',
          iconBg: 'bg-blue-100 dark:bg-blue-900/50',
          text: 'text-gray-900 dark:text-gray-100'
        }
    }
  }

  const getIcon = (type: ToastMessage['type']) => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        )
      case 'error':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )
      case 'info':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        )
      case 'warning':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )
    }
  }

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-3 pointer-events-none">
      {toasts.map((toast) => {
        const styles = getToastStyles(toast.type)
        const exiting = isExiting.has(toast.id)
        
        return (
          <div
            key={toast.id}
            className={`
              ${styles.bg} ${styles.border} ${styles.text}
              backdrop-blur-xl border rounded-2xl shadow-2xl
              min-w-[320px] max-w-[420px] p-4
              transform transition-all duration-300 ease-out pointer-events-auto
              ${exiting 
                ? 'translate-x-full opacity-0' 
                : 'translate-x-0 opacity-100 animate-slide-in-right'
              }
              hover:shadow-3xl hover:scale-[1.02]
            `}
            style={{
              boxShadow: '0 10px 40px -10px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.05) inset'
            }}
          >
            <div className="flex items-start gap-3">
              {/* Icon Container */}
              <div className={`
                ${styles.iconBg} ${styles.icon}
                p-2 rounded-xl flex-shrink-0
                shadow-sm
              `}>
                {getIcon(toast.type)}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-0.5">
                  {toast.title}
                </h4>
                {toast.description && (
                  <p className="text-xs opacity-75 leading-relaxed">
                    {toast.description}
                  </p>
                )}
              </div>
              
              {/* Close Button */}
              <button
                onClick={() => handleRemove(toast.id)}
                className={`
                  ${styles.text}
                  opacity-40 hover:opacity-70 transition-opacity
                  p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5
                  flex-shrink-0
                `}
                aria-label="Close notification"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black/5 dark:bg-white/5 rounded-b-2xl overflow-hidden">
              <div 
                className={`h-full ${styles.icon} opacity-30 animate-progress`}
                style={{
                  background: `linear-gradient(90deg, currentColor 0%, currentColor 100%)`,
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}